import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import Button from '~/components/common/Button'
import useCall from '~/hooks/useCall'
import useDebugger from '~/hooks/useDebugger'
import pickup from '~/services/pickup'

import ModalAudioPermission from './ModalAudioPermission'
import ModalCancelQueue from './ModalCancelQueue'

const CallAction = () => {
  const {
    isQueueStarted,
    isFirstQueueCalled,
    currentServing,
    nextQueue,
    loadingStates,
    callData,
    skipQueue,
    cancelQueue,
    selectedTicket,
    recallQueue,
    moveBackQueue,
    formRef,
    audioPermissionStatus,
    serveQueue,
  } = useCall()
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)
  const { debug } = useDebugger()
  const { queue_id } = currentServing.ticket

  // Batalkan hanya untuk antrean dari kode unik. Gagal/timeout = tombol tidak muncul;
  // BE tetap menolak cancel untuk antrean non kode unik.
  const linkedQuery = useQuery({
    queryKey: ['pickup-linked-by-queue', queue_id],
    queryFn: () => pickup.linkedByQueue({ queue_id }),
    enabled: !!queue_id && currentServing.isIdle,
    retry: false,
    staleTime: Infinity,
    onError: (error) => debug('info', 'API `pickup/linked-by-queue` failed:', error),
  })
  const isKodeUnikQueue = linkedQuery.data?.data?.linked === true

  const isNextDisabled =
    !isQueueStarted ||
    currentServing.isIdle ||
    callData.antrian_list.length === 0 ||
    selectedTicket.isSkip ||
    currentServing.isServing ||
    (!isFirstQueueCalled && !selectedTicket.queue_id)
  const isServeDisabled = !isQueueStarted || !currentServing.isIdle || currentServing.isServing
  const isAuxDisabled = !isQueueStarted || !currentServing.isIdle || !currentServing.isServing
  const isSkipDisaled = !isQueueStarted || !currentServing.isIdle
  const isCancelDisabled = !isQueueStarted || !currentServing.isIdle
  const isRecallDisabled = !isQueueStarted || !currentServing.isIdle || currentServing.isServing
  const isMoveBackDisabled =
    (!isQueueStarted && !currentServing.isIdle && currentServing.isIdle) ||
    !selectedTicket.isSkip ||
    currentServing.isServing

  const handleNextQueue = () => nextQueue()

  const handleServeQueue = () => serveQueue()

  const handleAuxQueue = () => {
    if (formRef.current) {
      formRef.current.requestSubmit()
    }
  }

  const handleSkipQueue = () => skipQueue()

  const handleCancelQueue = (reason: string) => cancelQueue(reason, () => setIsCancelModalOpen(false))

  const handleRecallQueue = () => recallQueue()

  const handleMoveBackQueue = () => moveBackQueue()

  return (
    <>
      <ModalAudioPermission isOpen={audioPermissionStatus !== 'granted'} />
      <ModalCancelQueue
        isOpen={isCancelModalOpen}
        isLoading={loadingStates.cancel}
        queueNo={currentServing.ticket.queue_no}
        closeModal={() => setIsCancelModalOpen(false)}
        confirmCancel={handleCancelQueue}
      />
      <div className="flex h-full flex-col gap-4">
        <Button
          type="button"
          variant="primary"
          size="large"
          isLoading={loadingStates.next}
          disabled={isNextDisabled}
          onClick={handleNextQueue}
        >
          Next
        </Button>
        <Button
          type="button"
          variant="primary"
          size="large"
          plain
          isLoading={loadingStates.serve}
          disabled={isServeDisabled}
          onClick={handleServeQueue}
        >
          Serve
        </Button>
        <Button type="button" variant="primary" size="large" disabled={isAuxDisabled} onClick={handleAuxQueue}>
          DONE
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="large"
          isLoading={loadingStates.skip}
          disabled={isSkipDisaled}
          onClick={handleSkipQueue}
        >
          Skip
        </Button>
        <Button
          type="button"
          variant="primary"
          size="large"
          isLoading={loadingStates.recall}
          disabled={isRecallDisabled}
          onClick={handleRecallQueue}
        >
          Recall
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="large"
          plain
          disabled={isMoveBackDisabled}
          onClick={handleMoveBackQueue}
        >
          Move Back
        </Button>
        {isKodeUnikQueue && (
          <Button
            type="button"
            variant="secondary"
            size="large"
            disabled={isCancelDisabled}
            onClick={() => setIsCancelModalOpen(true)}
          >
            Batalkan
          </Button>
        )}
      </div>
    </>
  )
}

export default CallAction
